import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack, Typography,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import boddVariables from '../../../variables.module.scss';
import FilterButton from '../../ui/Filterbutton';
import FilterBadge from '../../ui/FilterBadge';

export interface Filters {
    garmentType: string;
    fits: string[];
}

interface SizeMatchFilterProps {
    fits: string[];
    garmentTypes: string[];
    setSelectedFilters: (filters: Filters) => void;
}

const SizeMatchFilter: FC<SizeMatchFilterProps> = (props) => {
    const { garmentTypes, fits, setSelectedFilters } = props;
    const [selectedGarmentType, setSelectedGarmentType] = useState<string>('');
    const [selectedFits, setSelectedFits] = useState<string[]>([]);
    const [filterCount, setFilterCount] = useState<number>(0);

    useEffect(() => {
        // Update parent component when local state changes
        setSelectedFilters({
            garmentType: selectedGarmentType,
            fits: selectedFits,
        });
        // Calculate filter count
        const count = (selectedGarmentType ? 1 : 0) + selectedFits.length;
        setFilterCount(count);
    }, [selectedGarmentType, selectedFits, setSelectedFilters]);

    const handleGarmentChange = (event: SelectChangeEvent) => {
        const garmentType = event.target.value as string;
        setSelectedGarmentType(garmentType);
        // Reset selected fits when garment type changes
        setSelectedFits([]);
    };

    const handleFitsChange = (fit: string) => {
        setSelectedFits((prev) => (prev.includes(fit) ? prev.filter((selectedFit) => selectedFit !== fit) : [...prev, fit]));
    };

    const handleClear = () => {
        setSelectedGarmentType('');
        setSelectedFits([]);
    };
    return (
        <div data-testid="SizeMatchFilter">
            <Stack
                direction="row"
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <FilterBadge isActive={filterCount > 0}>
                    {filterCount > 0 && (
                        <Typography
                            variant="body1"
                            component="span"
                            ml={1}
                            fontWeight="bold"
                        >
                            {filterCount}
                        </Typography>
                    )}
                </FilterBadge>
                <Button
                    variant="text"
                    startIcon={<ClearIcon />}
                    sx={{
                        color: boddVariables.grey600,
                    }}
                    onClick={handleClear}
                >
                    Clear
                </Button>
            </Stack>
            <div
                style={{ position: 'relative', width: '100%' }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        pt: 2,
                        pb: 1,
                        alignItems: 'center',
                        overflowX: 'scroll',
                        overflowY: 'visible',
                        width: '100%',
                        whiteSpace: 'nowrap',
                        '&::-webkit-scrollbar': {
                            display: 'none', // hide scrollbar for WebKit browsers
                        },
                        msOverflowStyle: 'none', // hide scrollbar for IE and Edge
                        scrollbarWidth: 'none', // hide scrollbar for Firefox
                    }}
                >
                    <Box
                        component="div"
                        sx={{
                            flexDirection: 'row',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <FormControl
                            size="small"
                            sx={{
                                width: 'fit-content',
                                minWidth: '150px',
                            }}
                        >
                            <InputLabel>Garment type</InputLabel>
                            <Select
                                label="Garment type"
                                value={selectedGarmentType}
                                onChange={handleGarmentChange}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {garmentTypes.map((garmentType) => (
                                    <MenuItem
                                        key={garmentType}
                                        value={garmentType}
                                    >
                                        {garmentType}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {fits.map((fit) => (
                            <FilterButton
                                key={fit}
                                filter={fit}
                                isSelected={selectedFits.includes(fit)}
                                onClick={() => handleFitsChange(fit)}
                            />
                        ))}
                    </Box>
                </Stack>
            </div>
        </div>
    );
};

export default SizeMatchFilter;
